import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Upload, FileSpreadsheet, Info, CheckCircle, AlertCircle, Loader2, FileText } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';
import { ErpPickSlipFetchCard } from './components/ErpPickSlipFetchCard';

const FGPickSlipUpload = () => {
  const navigate = useNavigate();
  const [selectedFile, setSelectedFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  
  // Progress tracking state
  const [importProgress, setImportProgress] = useState({
    isImporting: false,
    currentPhase: '',
    totalRecords: 0,
    processedRecords: 0,
    importedCount: 0,
    skippedCount: 0,
    errors: [],
    percentage: 0
  });

  const resetProgress = () => {
    setImportProgress({
      isImporting: false,
      currentPhase: '',
      totalRecords: 0,
      processedRecords: 0,
      importedCount: 0,
      skippedCount: 0,
      errors: [],
      percentage: 0
    });
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const validExtensions = ['.csv', '.xlsx', '.xls'];
      const ext = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
      if (!validExtensions.includes(ext)) {
        toast.error('Please select a CSV or Excel file');
        return;
      }
      setSelectedFile(file);
      resetProgress();
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      toast.error('Please select a file');
      return;
    }

    setIsUploading(true);
    setImportProgress(prev => ({ ...prev, isImporting: true, currentPhase: 'uploading', percentage: 5 }));

    try {
      const formData = new FormData();
      formData.append('file', selectedFile);

      const token = localStorage.getItem('session_token');
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      // Phase 5 multi-site: raw fetch() bypasses apiClient — inject site header.
      const activeSiteId = localStorage.getItem('active_site_id') || 'salem';

      // Use AbortController for timeout handling
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 300000); // 5 minute timeout
      
      const response = await fetch(`${backendUrl}/api/wms/fg/pick-slips/import`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Site-Id': activeSiteId,
        },
        body: formData,
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      // Handle non-OK responses
      if (!response.ok) {
        let errorMessage = `Upload failed with status ${response.status}`;
        try {
          // Try to parse error from response body
          const errorData = await response.json();
          errorMessage = errorData.detail || errorData.message || errorMessage;
        } catch {
          try {
            const errorText = await response.text();
            errorMessage = errorText || errorMessage;
          } catch {
            // Body might already be consumed, use status message
          }
        }
        throw new Error(errorMessage);
      }

      setImportProgress(prev => ({ ...prev, currentPhase: 'importing', percentage: 15 }));

      // Check if response is a streaming response
      if (!response.body) {
        throw new Error('Server did not return a streaming response');
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));
              
              if (data.error) {
                setImportProgress(prev => ({ 
                  ...prev, 
                  currentPhase: 'error',
                  errors: [data.error]
                }));
                toast.error(data.error);
                return;
              }

              if (data.phase === 'completed') {
                setImportProgress(prev => ({ 
                  ...prev, 
                  currentPhase: 'completed',
                  percentage: 100,
                  processedRecords: data.processed || 0,
                  importedCount: data.imported || 0,
                  skippedCount: data.skipped || 0,
                  totalRecords: data.total || 0,
                  errors: data.errors || []
                }));
                toast.success(`Imported ${data.imported} pick slip(s) (${data.skipped} skipped)`);
              } else {
                setImportProgress(prev => ({ 
                  ...prev, 
                  currentPhase: 'importing',
                  percentage: Math.max(15, data.percentage || 0),
                  processedRecords: data.processed || 0,
                  importedCount: data.imported || 0,
                  skippedCount: data.skipped || 0,
                  totalRecords: data.total || prev.totalRecords,
                  message: data.message || ''
                }));
              }
            } catch (e) {
              console.log('Parse error:', e);
            }
          }
        }
      }
    } catch (error) {
      console.error('Import error:', error);
      if (error.name === 'AbortError') {
        setImportProgress(prev => ({ 
          ...prev, 
          currentPhase: 'error',
          errors: ['Import timed out. Please try with a smaller file.']
        }));
        toast.error('Import timed out. Please try with a smaller file.');
      } else {
        setImportProgress(prev => ({ 
          ...prev, 
          currentPhase: 'error',
          errors: [error.message || 'Import failed']
        }));
        toast.error(error.message || 'Import failed');
      }
    } finally {
      setIsUploading(false);
    }
  };

  const handleNewImport = () => {
    resetProgress();
    setSelectedFile(null);
    const fileInput = document.querySelector('input[type="file"]');
    if (fileInput) fileInput.value = '';
  };

  const getPhaseLabel = () => {
    switch (importProgress.currentPhase) {
      case 'uploading': return 'Uploading file...';
      case 'importing': return 'Importing to database...';
      case 'completed': return 'Import completed!';
      case 'error': return 'Import failed';
      default: return 'Ready to import';
    }
  };

  const getPhaseColor = () => {
    switch (importProgress.currentPhase) {
      case 'completed': return 'text-green-600';
      case 'error': return 'text-red-600';
      default: return 'text-[#2D5F4F]';
    }
  };

  const requiredColumns = ['PickSlip', 'Barcode', 'PickSlip Quantity'];
  const optionalColumns = ['Created User', 'Created Date', 'Stockpoint', 'Warehouse', 'Customer', 'State', 'Category', 'Agent', 'Order', 'Design Name', 'Description', 'Shade', 'Size', 'DC Quantity', 'Is Urgent?', 'Remarks'];

  return (
    <div className="min-h-screen bg-[#FFFBF5]">
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center">
          <Button
            variant="ghost"
            onClick={() => navigate('/wms/fg')}
            className="text-[#F9FAFB] hover:bg-white/10 mr-4"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-xl font-bold font-display">Upload Pick Slips</h1>
            <p className="text-sm text-[#E8DCC8]">Import FG pick slips from accounting</p>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 md:px-6 py-8">
        <ErpPickSlipFetchCard />
        <div className="bg-white rounded-lg border border-[#E5DFD0] p-8">
          <h2 className="text-lg font-semibold text-[#1A1F2E] mb-6 font-display">Upload File</h2>
          
          <div className="space-y-6">
            {/* File Selection */}
            <div>
              <Label>Select File (CSV or Excel)</Label>
              <div className="mt-2">
                <label className={`flex items-center justify-center h-32 px-4 border-2 border-dashed border-[#E5DFD0] rounded-lg transition-colors ${
                  importProgress.isImporting ? 'cursor-not-allowed opacity-60' : 'cursor-pointer hover:border-[#2D5F4F] hover:bg-[#F5F1E8]'
                }`}>
                  <div className="text-center">
                    <FileText className="w-10 h-10 mx-auto mb-2 text-[#5A5F6E]" />
                    <span className="text-sm text-[#5A5F6E]">
                      {selectedFile ? selectedFile.name : 'Click to select file or drag & drop'}
                    </span>
                    {selectedFile && (
                      <p className="text-xs text-gray-400 mt-1">
                        {(selectedFile.size / 1024).toFixed(1)} KB
                      </p>
                    )}
                  </div>
                  <input
                    type="file"
                    accept=".csv,.xlsx,.xls"
                    onChange={handleFileChange}
                    className="hidden"
                    disabled={importProgress.isImporting}
                    data-testid="file-input"
                  />
                </label>
              </div>
            </div>

            {/* Progress Section */}
            {importProgress.isImporting || importProgress.currentPhase === 'completed' || importProgress.currentPhase === 'error' ? (
              <div className="space-y-4 p-6 bg-[#F5F1E8] rounded-lg border border-[#E5DFD0]">
                {/* Phase Label */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    {importProgress.currentPhase === 'completed' ? (
                      <CheckCircle className="w-5 h-5 text-green-600" />
                    ) : importProgress.currentPhase === 'error' ? (
                      <AlertCircle className="w-5 h-5 text-red-600" />
                    ) : (
                      <Loader2 className="w-5 h-5 text-[#2D5F4F] animate-spin" />
                    )}
                    <span className={`font-medium ${getPhaseColor()}`}>
                      {getPhaseLabel()}
                    </span>
                  </div>
                  <span className="text-sm text-[#5A5F6E]">
                    {importProgress.processedRecords} / {importProgress.totalRecords} records
                  </span>
                </div>

                {/* Progress Bar */}
                <div className="space-y-2">
                  <Progress 
                    value={importProgress.percentage} 
                    className="h-3"
                    data-testid="import-progress"
                  />
                  <div className="flex justify-between text-xs text-[#5A5F6E]">
                    <span>{importProgress.percentage}% complete</span>
                  </div>
                </div>

                {/* Stats */}
                {importProgress.currentPhase === 'completed' && (
                  <div className="grid grid-cols-2 gap-4 pt-2">
                    <div className="text-center p-3 bg-green-50 rounded-lg">
                      <p className="text-2xl font-bold text-green-600">{importProgress.importedCount}</p>
                      <p className="text-xs text-green-700">Pick Slips Imported</p>
                    </div>
                    <div className="text-center p-3 bg-gray-50 rounded-lg">
                      <p className="text-2xl font-bold text-gray-600">{importProgress.skippedCount}</p>
                      <p className="text-xs text-gray-700">Skipped (Duplicates)</p>
                    </div>
                  </div>
                )}

                {/* Errors */}
                {importProgress.errors.length > 0 && (
                  <div className="mt-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-sm font-medium text-red-800 mb-1">Errors:</p>
                    <ul className="text-xs text-red-700 list-disc list-inside">
                      {importProgress.errors.slice(0, 5).map((err, idx) => (
                        <li key={idx}>{err}</li>
                      ))}
                      {importProgress.errors.length > 5 && (
                        <li>...and {importProgress.errors.length - 5} more</li>
                      )}
                    </ul>
                  </div>
                )}

                {/* Actions */}
                {importProgress.currentPhase === 'completed' && (
                  <div className="flex gap-2 pt-2">
                    <Button
                      onClick={handleNewImport}
                      variant="outline"
                      className="flex-1"
                    >
                      Upload Another File
                    </Button>
                    <Button
                      onClick={() => navigate('/wms/fg-pick-slips')}
                      className="flex-1 bg-[#2D5F4F] hover:bg-[#234A3D] text-white"
                    >
                      View Pick Slips
                    </Button>
                  </div>
                )}
              </div>
            ) : (
              <Button
                onClick={handleUpload}
                disabled={!selectedFile || isUploading}
                className="w-full h-12 bg-[#2D5F4F] hover:bg-[#234A3D] text-white"
                data-testid="upload-button"
              >
                <Upload className="w-5 h-5 mr-2" />
                {isUploading ? 'Processing...' : 'Upload & Import'}
              </Button>
            )}
          </div>
        </div>

        {/* Required Columns */}
        <div className="bg-white rounded-lg border border-[#E5DFD0] p-8 mt-6">
          <h3 className="text-lg font-semibold text-[#1A1F2E] mb-4 font-display">Required Columns</h3>
          <div className="flex flex-wrap gap-2 mb-4">
            {requiredColumns.map((col, idx) => (
              <span
                key={idx}
                className="px-4 py-2 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 font-medium"
              >
                {col}
              </span>
            ))}
          </div>
          
          <h4 className="text-md font-medium text-[#1A1F2E] mb-2 mt-4">Optional Columns</h4>
          <div className="flex flex-wrap gap-2">
            {optionalColumns.map((col, idx) => (
              <span
                key={idx}
                className="px-3 py-1 bg-[#F5F1E8] border border-[#E5DFD0] rounded-lg text-xs text-[#5A5F6E]"
              >
                {col}
              </span>
            ))}
          </div>
        </div>

        {/* Info Card */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mt-6">
          <div className="flex gap-3">
            <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm text-blue-900 font-medium mb-2">
                Import Tips:
              </p>
              <ul className="text-sm text-blue-800 list-disc list-inside space-y-1">
                <li>Export pick slips from your accounting software as CSV or Excel</li>
                <li>Ensure the file has all required columns</li>
                <li>Duplicate pick slip numbers will be skipped automatically</li>
                <li>Large files (100K+ rows) may take a few minutes to process</li>
              </ul>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default FGPickSlipUpload;
